import { CURRENCIES } from "@cvx/schema";
import type { ClassValue } from "clsx";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useAccount } from "@/state/useAccount";
import { useAuthActions } from "@convex-dev/auth/react";

/**
 * Tailwind CSS classnames with support for conditional classes.
 * Widely used for Radix components.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns a function that calls all of its arguments.
 */
export function callAll<Args extends unknown[]>(
  ...fns: (((...args: Args) => unknown) | undefined)[]
) {
  return (...args: Args) => fns.forEach((fn) => fn?.(...args));
}

/**
 * Locales.
 */
export function getLocaleCurrency() {
  return navigator.languages.includes("en-US")
    ? CURRENCIES.USD
    : CURRENCIES.EUR;
}

export const useSignOut = () => {
  const { signOut } = useAuthActions();
  const { clearAccount } = useAccount();

  return async () => {
    // Clear account state first
    clearAccount();

    // Sign out from Convex auth
    await signOut();

    // Wait a tick to ensure zustand persist middleware finishes writing
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Clear all storage - including the zustand persisted state
    localStorage.clear();
    sessionStorage.clear();

    // Clear again just to be absolutely sure (catches any race condition writes)
    // This is especially important for the zustand persist middleware
    await new Promise((resolve) => setTimeout(resolve, 10));
    localStorage.clear();
    sessionStorage.clear();

    // Use window.location for a hard redirect to ensure clean state
    window.location.href = "/login";
  };
};
